import { useRef, useState } from "react";
import { FileText, Upload, Trash2, File } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AGENT_SETTINGS } from "@/constants/testIds";

const ACCEPTED = ".pdf,.docx,.txt";

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Documentos (PDF/Word/TXT) subidos para la knowledge base del agente:
 * al subirlos se extrae el texto automaticamente y se incorpora al contexto
 * del agente (ver backend `agent/kb_utils.py`). */
export default function DocumentsSection({ documents, onUpload, onDelete }) {
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await onDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-foreground">Documentos</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Subi folletos, protocolos o listas de precios en PDF, Word o TXT. El agente usa su contenido para responder.</p>
        </div>
        <input ref={fileInputRef} type="file" accept={ACCEPTED} className="hidden" onChange={handleFileChange} data-testid={AGENT_SETTINGS.documentUploadInput} />
        <Button variant="outline" className="gap-2 shrink-0" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid={AGENT_SETTINGS.documentUploadButton}>
          <Upload className="w-4 h-4" /> {uploading ? "Subiendo..." : "Subir archivo"}
        </Button>
      </div>

      {documents.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-6 text-center" data-testid={AGENT_SETTINGS.documentsEmptyState}>
          <FileText className="w-7 h-7 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavia no subiste ningun documento.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl divide-y divide-border" data-testid={AGENT_SETTINGS.documentsList}>
          {documents.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 p-3" data-testid={`${AGENT_SETTINGS.documentRowPrefix}${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <File className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-foreground truncate">{d.filename}</p>
                  <p className="text-xs text-muted-foreground">{formatSize(d.size)} · {d.char_count.toLocaleString()} caracteres extraidos</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(d)} data-testid={`${AGENT_SETTINGS.documentDeleteButtonPrefix}${d.id}`}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar este documento?</AlertDialogTitle>
            <AlertDialogDescription>El agente dejara de usar "{deleteTarget?.filename}" para responder. Esta accion no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
